import React from "react";
import { SelectDropdown } from "../../../../../components/ui/dropdowns/SelectDropdown";
import { cn } from "../../../../../lib/utils";

const splitNumberPrefix = (label: string): { prefix?: string; rest: string } => {
  const match = label.match(/^(\d[\w.()]*?)\s+(.*)$/);
  if (!match) return { rest: label };
  return { prefix: match[1], rest: match[2] };
};

const renderRequiredMarkers = (text: string): React.ReactNode => {
  if (!text.includes("**")) return text;
  const parts = text.split("**");
  return (
    <>
      {parts.map((p, idx) => (
        <React.Fragment key={idx}>
          {p}
          {idx < parts.length - 1 ? <span className="text-red-500">**</span> : null}
        </React.Fragment>
      ))}
    </>
  );
};

const renderLeftCellLabel = (label: React.ReactNode): React.ReactNode => {
  if (typeof label !== "string" && typeof label !== "number") return label;
  const raw = String(label);
  const { prefix, rest } = splitNumberPrefix(raw);

  if (prefix) {
    return (
      <span className="grid w-full grid-cols-[auto,1fr] gap-x-2">
        <span className="shrink-0">{prefix}</span>
        <span className="min-w-0 whitespace-pre-wrap">{renderRequiredMarkers(rest)}</span>
      </span>
    );
  }

  return <span className="whitespace-pre-wrap">{renderRequiredMarkers(raw)}</span>;
};

export type FmrTwoColumnTableRowProps<TData extends Record<string, any>> = {
  /**
   * Left column content (label). Can include numbering and required markers (**).
   */
  label: React.ReactNode;
  rightSectionLabel?: React.ReactNode;
  /**
   * Optional data key to read value from `data`.
   * If `render` is provided, it takes precedence.
   */
  key?: keyof TData;
  /**
   * Field type -> determines what control renders on the right.
   */
  type?: "text" | "date" | "number" | "dropdown" | "custom";
  /**
   * For dropdown fields
   */
  options?: Array<{ name: string; value: any }>;
  placeholder?: string;
  /**
   * Optional explicit renderer for the right cell.
   */
  render?: (args: {
    value: any;
    onChange: (v: any) => void;
    disabled: boolean;
    row: FmrTwoColumnTableRowProps<TData>;
  }) => React.ReactNode;
  /**
   * "section" rows represent group headings (e.g. "3 Details of the branch**")
   * and render an empty right cell by default.
   */
  kind?: "field" | "section";
  /**
   * Align content to top instead of center (useful for multiline inputs).
   */
  alignTop?: boolean;
  /**
   * Disable this field specifically (useful when Editable = No).
   */
  disabled?: boolean;
};

export interface FmrTwoColumnTableProps<TData extends Record<string, any>> {
  header?: React.ReactNode;
  data: TData;
  onChange: (key: keyof TData, value: any) => void;
  rows: Array<FmrTwoColumnTableRowProps<TData>>;
  className?: string;
  /**
   * Left column width (matches Figma specs per table).
   */
  leftWidthPx: number;
  /**
   * Row height (defaults to 44 as per designs).
   */
  rowHeightPx?: number;
  /**
   * If true, renders a border around the table.
   */
  bordered?: boolean;
  /**
   * Disable all fields in this table (future use).
   */
  disabled?: boolean;
  /**
   * Disable specific fields by key (future use).
   */
  disabledKeys?: Partial<Record<keyof TData, boolean>>;
}

const FmrTwoColumnTable = <TData extends Record<string, any>>({
  header,
  data,
  onChange,
  rows,
  className,
  leftWidthPx,
  rowHeightPx = 44,
  bordered = true,
  disabled = false,
  disabledKeys,
}: FmrTwoColumnTableProps<TData>) => {
  const getFieldDisabled = (row: FmrTwoColumnTableRowProps<TData>) => {
    if (row.disabled !== undefined) return row.disabled;
    if (row.key && disabledKeys?.[row.key] !== undefined) return !!disabledKeys[row.key];
    return disabled;
  };

  const getValue = (key?: keyof TData) => (key ? (data as any)[key] : undefined);

  const renderInput = (row: FmrTwoColumnTableRowProps<TData>) => {
    const key = row.key;
    const value = getValue(key);
    const fieldDisabled = getFieldDisabled(row);
    const type = row.type ?? "text";

    const setValue = (v: any) => {
      if (!key) return;
      onChange(key, v);
    };

    if (row.render) {
      return row.render({
        value,
        onChange: setValue,
        disabled: fieldDisabled,
        row,
      });
    }

    if (!key) return null;

    if (type === "date") {
      return (
        <input
          type="date"
          value={value ?? ""}
          disabled={fieldDisabled}
          onChange={(e) => setValue(e.target.value)}
          className={cn(
            "w-full bg-transparent",
            "font-sans font-normal text-sm leading-5",
            "text-[#0A0A0A] placeholder:text-[#737373]",
            "focus:outline-none",
            fieldDisabled && "opacity-50 cursor-not-allowed",
          )}
        />
      );
    }

    if (type === "number") {
      return (
        <input
          type="number"
          value={value ?? ""}
          disabled={fieldDisabled}
          onChange={(e) => setValue(e.target.value === "" ? null : e.target.value)}
          className={cn(
            "w-full bg-transparent",
            "font-sans font-normal text-sm leading-5",
            "text-[#0A0A0A] placeholder:text-[#737373]",
            "focus:outline-none",
            fieldDisabled && "opacity-50 cursor-not-allowed",
          )}
        />
      );
    }

    if (type === "dropdown") {
      const options = row.options ?? [];
      return (
        <SelectDropdown<string>
          value={(value ?? null) as any}
          options={options.map((o) => ({
            name: o.name,
            value: String(o.value),
          }))}
          placeholder={row.placeholder ?? "Select..."}
          showSearch={options.length > 8}
          allowClear={false}
          onChange={(v) => setValue(v ?? "")}
          disabled={fieldDisabled}
          wrapText
          className="w-full flex-1 min-w-0"
          buttonClassName={cn(
            "w-full flex-1 min-w-0",
            "px-0 py-0 border-0 rounded-none bg-transparent hover:bg-transparent ring-0",
            "text-[#0A0A0A] data-[state=open]:bg-transparent",
          )}
        />
      );
    }

    // default: text
    const currentText = value === undefined || value === null ? "" : String(value);
    return (
      <input
        value={currentText}
        disabled={fieldDisabled}
        placeholder={row.placeholder ?? ""}
        onChange={(e) => {
          // Allow spaces while typing; trimming is handled onBlur (see LeaDetails pattern)
          setValue(e.target.value);
        }}
        onBlur={(e) => {
          const trimmed = e.target.value.trim();
          if (trimmed !== e.target.value) setValue(trimmed);
        }}
        className={cn(
          "w-full flex-1 min-w-0 bg-transparent",
          "font-sans font-normal text-sm leading-5",
          "text-[#0A0A0A] placeholder:text-[#737373]",
          "focus:outline-none",
          fieldDisabled && "opacity-50 cursor-not-allowed",
        )}
      />
    );
  };

  return (
    <div className={cn("w-full overflow-visible", className)}>
      {header ? (
        <div className="mb-3">
          <div className="font-sans font-medium text-base leading-6 text-[#0A0A0A]">{header}</div>
        </div>
      ) : null}

      <div className={cn("w-full overflow-visible", bordered && "border border-[#E5E5E5]")}>
        {rows.map((row, idx) => {
          const isLast = idx === rows.length - 1;
          const isSection = row.kind === "section";
          return (
            <div key={idx} className={cn("flex w-full", !isLast && "border-b border-[#E5E5E5]")}>
              {/* Left cell */}
              <div
                className={cn(
                  "p-2 py-1.5 flex gap-2 font-sans text-sm leading-5 shrink-0",
                  row.alignTop ? "items-start" : "items-center",
                  isSection ? "bg-[#E5E7EB]" : "bg-[#F1F5F9]",
                )}
                style={{
                  width: leftWidthPx,
                  flexShrink: 0,
                  minWidth: 85,
                  minHeight: rowHeightPx,
                }}
              >
                <span className={cn("text-[#0A0A0A] whitespace-pre-wrap font-medium")}>
                  {renderLeftCellLabel(row.label)}
                </span>
              </div>

              {/* Right cell */}
              <div
                className={cn(
                  "flex-1 p-2 font-sans text-sm leading-5 text-[#0A0A0A]",
                  row.alignTop ? "items-start" : "items-center",
                  isSection ? "bg-[#E5E7EB]" : "bg-white",
                  "flex",
                )}
                style={{ minHeight: rowHeightPx }}
              >
                {isSection ? (
                  <span className={cn("text-[#0A0A0A] whitespace-pre-wrap", isSection && "font-medium")}>
                    {renderLeftCellLabel(row?.rightSectionLabel)}
                  </span>
                ) : (
                  <div className="w-full flex-1 min-w-0">{renderInput(row)}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FmrTwoColumnTable;
